import React, { useEffect, useState } from 'react';
import { Snackbar, Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  Avatar,
  Checkbox,
  FormControlLabel, } from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  WbSunny as SunIcon,
  Nightlight as MoonIcon,
  FileDownload as DownloadIcon,
  RestaurantMenu as RestaurantMenuIcon,
} from '@mui/icons-material';
import { toJalaali, toGregorian } from 'jalaali-js';
import * as XLSX from 'xlsx';

import { toEnglishNumber, toPersianNumber } from '../utils/persianNumber';
import { MenuItem, menuItemsAPI, mealsAPI, Meal, systemSettingsAPI } from '../services/api';

const DailyMealPlanning: React.FC = () => {
  // Format date as YYYY-MM-DD without timezone shift
  const formatDateLocal = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  };


  const [meals, setMeals] = useState<Meal[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const initialForm = {
    mealId: '',
    maxReservations: 100,
    isCorporate: false,
    price: 0,
    corporatePrice: 0,
  };

  const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner'>('lunch');
  const [foodForm, setFoodForm] = useState(initialForm);
  const [appetizerForm, setAppetizerForm] = useState(initialForm);
  const [beverageForm, setBeverageForm] = useState(initialForm);
  const [selectedDate, setSelectedDate] = useState(formatDateLocal(new Date()));
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // New state to hold meals added in the current session (locally) before hitting save
  const [pendingItems, setPendingItems] = useState<Partial<MenuItem>[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // وضعیت‌های جدید برای گزینه‌های تکرار
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatDaily, setRepeatDaily] = useState(false);

  const [holidaysMap, setHolidaysMap] = useState<Record<string, {title: string, isHoliday: boolean}[]>>({});

  useEffect(() => {
    systemSettingsAPI.getHolidays().then(setHolidaysMap);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Calculate start and end of the Persian month currently viewed
      const jDate = toJalaali(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        currentMonth.getDate()
      );

      const persianYear = jDate.jy;
      const persianMonth = jDate.jm;

      // Calculate days in Persian month
      let daysInPersianMonth: number;
      if (persianMonth <= 6) {
        daysInPersianMonth = 31;
      } else if (persianMonth <= 11) {
        daysInPersianMonth = 30;
      } else {
        const isLeap = ((persianYear + 2346) % 128) < 30;
        daysInPersianMonth = isLeap ? 30 : 29;
      }

      const startGregorian = toGregorian(persianYear, persianMonth, 1);
      const endGregorian = toGregorian(persianYear, persianMonth, daysInPersianMonth);

      const startDate = new Date(startGregorian.gy, startGregorian.gm - 1, startGregorian.gd);
      const endDate = new Date(endGregorian.gy, endGregorian.gm - 1, endGregorian.gd);

      const [mealsData, menuData] = await Promise.all([
        mealsAPI.getMeals(),
        menuItemsAPI.getMenuItems(
          formatDateLocal(startDate),
          formatDateLocal(endDate)
        ),
      ]);
      setMeals(mealsData);
      setMenuItems(menuData);
    } catch (err: any) {
      setError('خطا در بارگذاری داده‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date: string, type?: 'breakfast' | 'lunch' | 'dinner') => {
    setSelectedDate(date);
    setMealType(type || 'lunch');
    setFoodForm(initialForm);
    setAppetizerForm(initialForm);
    setBeverageForm(initialForm);
    setPendingItems([]); // Clear any unsaved pending items when opening a new date/type
    // ریست کردن چک‌باکس‌ها در هنگام باز شدن پاپ‌آپ جدید
    setRepeatWeekly(false);
    setRepeatDaily(false);
    setOpenModal(true);
    setError('');
    setSuccess('');
  };

  const handleAddItem = (type: 'food' | 'appetizer' | 'beverage') => {
    const currentForm = type === 'food' ? foodForm : type === 'appetizer' ? appetizerForm : beverageForm;
    if (!currentForm.mealId) {
      setError('لطفا آیتم مورد نظر را انتخاب کنید');
      return;
    }

    setError('');
    setSuccess('');

    const slotRealItems = menuItems.filter(i => i.date.startsWith(selectedDate) && i.mealType === mealType);
    const slotPendingItems = pendingItems.filter(i => i.date === selectedDate && i.mealType === mealType);

    const hasExactReal = slotRealItems.some(i => i.mealId === currentForm.mealId);
    const hasExactPending = slotPendingItems.some(i => i.mealId === currentForm.mealId);

    if (hasExactReal || hasExactPending) {
       setError('این مورد برای تاریخ انتخابی از قبل وجود دارد');
       return;
    }

    setPendingItems([
      ...pendingItems,
      {
        id: `pending-${selectedDate}-${Date.now()}-${Math.random()}`,
        mealId: currentForm.mealId,
        date: selectedDate,
        mealType: mealType,
        maxReservations: currentForm.maxReservations,
        isCorporate: currentForm.isCorporate,
        price: currentForm.price,
        corporatePrice: currentForm.corporatePrice,
        currentReservations: 0,
      }
    ]);

    if (type === 'food') setFoodForm(initialForm);
    else if (type === 'appetizer') setAppetizerForm(initialForm);
    else setBeverageForm(initialForm);
  };

  const handleSaveMenu = async () => {
    const hasPending = pendingItems.length > 0;
    const isRepeatChecked = repeatWeekly || repeatDaily;

    if (!hasPending && !isRepeatChecked) {
      setOpenModal(false);
      return;
    }

    // Validation: Exactly one corporate meal per day/mealType
    const existing = getMenuItemsForDate(selectedDate).filter(i => i.mealType === mealType);
    const localPending = pendingItems.filter(i => i.date === selectedDate && i.mealType === mealType);
    const allItems = [...existing, ...localPending];
    const corporateCount = allItems.filter(i => i.isCorporate).length;

    if (corporateCount !== 1) {
      setShowConfirmDialog(true);
      return;
    }

    await performBatchSave();
  };

  const performBatchSave = async () => {
    setShowConfirmDialog(false);
    setError('');
    setSuccess('');
    try {
      // 1. Gather all items for the selectedDate (already saved + locally pending)
      const existingOnSelectedDate = getMenuItemsForDate(selectedDate).filter(i => i.mealType === mealType);
      const pendingOnSelectedDate = pendingItems.filter(i => i.date === selectedDate && i.mealType === mealType);
      const sourceItems = [...existingOnSelectedDate, ...pendingOnSelectedDate];

      // 2. Identify the target dates to repeat this menu to
      let targetDates: string[] = [];
      if (repeatDaily || repeatWeekly) {
        const selDateObj = new Date(selectedDate);
        selDateObj.setHours(0, 0, 0, 0);
        const selDayOfWeek = selDateObj.getDay();

        const allDays = getDaysInMonth().filter(d => d !== null) as { date: Date, persianDay: number }[];
        allDays.forEach(d => {
          const dDateObj = new Date(d.date);
          dDateObj.setHours(0, 0, 0, 0);
          const dStr = formatDateLocal(dDateObj);
          
          if (dDateObj > selDateObj) { // only repeat for future dates in the month
            if (repeatDaily) {
              targetDates.push(dStr);
            } else if (repeatWeekly && dDateObj.getDay() === selDayOfWeek) {
              targetDates.push(dStr);
            }
          }
        });
      }

      // 3. Save the pending items for the selectedDate itself
      for (const item of pendingOnSelectedDate) {
        await menuItemsAPI.createMenuItem({
          mealId: item.mealId!,
          date: selectedDate,
          mealType: mealType,
          maxReservations: item.maxReservations!,
          isCorporate: item.isCorporate!,
          price: item.price!,
          corporatePrice: item.corporatePrice!,
        });
      }

      // 4. Duplicate the sourceItems to the targetDates
      for (const dStr of targetDates) {
        const targetRealItems = menuItems.filter(i => i.date.startsWith(dStr) && i.mealType === mealType);
        
        for (const sourceItem of sourceItems) {
          // Check if this meal is already on the target date
          const alreadyExists = targetRealItems.some(i => i.mealId === sourceItem.mealId);
          if (!alreadyExists) {
            await menuItemsAPI.createMenuItem({
              mealId: sourceItem.mealId!,
              date: dStr,
              mealType: mealType,
              maxReservations: sourceItem.maxReservations!,
              isCorporate: sourceItem.isCorporate!,
              price: sourceItem.price!,
              corporatePrice: sourceItem.corporatePrice!,
            });
          }
        }
      }

      // 5. Also save any other pending items that were manually added for other dates
      const otherPending = pendingItems.filter(i => i.date !== selectedDate || i.mealType !== mealType);
      for (const item of otherPending) {
        await menuItemsAPI.createMenuItem({
          mealId: item.mealId!,
          date: item.date!,
          mealType: item.mealType as any,
          maxReservations: item.maxReservations!,
          isCorporate: item.isCorporate!,
          price: item.price!,
          corporatePrice: item.corporatePrice!,
        });
      }

      setSuccess('منو با موفقیت ذخیره و تکرار شد');
      setPendingItems([]);
      setOpenModal(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره منو');
    }
  };

  const handleToggleCorporate = async (item: MenuItem | Partial<MenuItem>) => {
    try {
      // If it's a pending item, toggle locally
      if (item.id?.startsWith('pending-')) {
        const newPending = pendingItems.map(p => {
          if (p.id === item.id) return { ...p, isCorporate: !p.isCorporate };
          // Optional: Auto-uncheck others if turning on?
          if (!item.isCorporate && p.isCorporate) return { ...p, isCorporate: false };
          return p;
        });
        setPendingItems(newPending);
        return;
      }

      // If it's a real item, logic remains same (but consider pending items as well? Real API update handles real items)
      const dateItems = getMenuItemsForDate(selectedDate);
      const sameTypeItems = dateItems.filter(i => i.mealType === item.mealType && i.id !== item.id);

      if (!item.isCorporate) {
        for (const other of sameTypeItems) {
          if (other.isCorporate) {
            await menuItemsAPI.updateMenuItem(other.id, { isCorporate: false });
          }
        }
      }

      await menuItemsAPI.updateMenuItem(item.id!, { isCorporate: !item.isCorporate });
      loadData();
      setSuccess('وضعیت سازمانی به‌روزرسانی شد');
    } catch (err) {
      setError('خطا در به‌روزرسانی وضعیت سازمانی');
    }
  };

  const handleDelete = async (menuItemId: string) => {
    if (!window.confirm('آیا از حذف این غذای روزانه مطمئن هستید؟')) return;

    // If it's a pending item, just remove from array
    if (menuItemId.startsWith('pending-')) {
      setPendingItems(pendingItems.filter(i => i.id !== menuItemId));
      return;
    }

    try {
      await menuItemsAPI.deleteMenuItem(menuItemId);
      setSuccess('غذای روزانه با موفقیت حذف شد');
      loadData();
    } catch (err: any) {
      setError('خطا در حذف غذای روزانه');
    }
  };

  const getMenuItemsForDate = (date: string) => {
    return menuItems.filter((item) => {
      // Parse item.date as local date (YYYY-MM-DD string) to avoid timezone shift
      const itemDate = item.date.split('T')[0]; // Get YYYY-MM-DD part if it's an ISO string
      return itemDate === date;
    });
  };

  const getDaysInMonth = () => {
    // Convert current month to Persian
    const jDate = toJalaali(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      currentMonth.getDate()
    );

    // Get Persian month length
    const persianYear = jDate.jy;
    const persianMonth = jDate.jm;

    // Calculate days in Persian month
    // First 6 months have 31 days, months 7-11 have 30 days, month 12 has 29 or 30 (leap year)
    let daysInPersianMonth: number;
    if (persianMonth <= 6) {
      daysInPersianMonth = 31;
    } else if (persianMonth <= 11) {
      daysInPersianMonth = 30;
    } else {
      // Month 12 (Esfand) - check if leap year
      // In Persian calendar, leap year is when (year + 2346) mod 128 < 30
      const isLeap = ((persianYear + 2346) % 128) < 30;
      daysInPersianMonth = isLeap ? 30 : 29;
    }

    // Get first day of Persian month in Gregorian
    const firstDayGregorian = toGregorian(persianYear, persianMonth, 1);
    const firstDay = new Date(firstDayGregorian.gy, firstDayGregorian.gm - 1, firstDayGregorian.gd);

    // Calculate starting day of week (Saturday = 0 in Persian calendar)
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = (startingDayOfWeek + 1) % 7;

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of Persian month
    for (let day = 1; day <= daysInPersianMonth; day++) {
      const gregorian = toGregorian(persianYear, persianMonth, day);
      const date = new Date(gregorian.gy, gregorian.gm - 1, gregorian.gd);
      days.push({ date, persianDay: day });
    }

    return days;
  };

  const getPersianDate = () => {
    const jDate = toJalaali(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      currentMonth.getDate()
    );
    const months = [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
    return {
      month: months[jDate.jm - 1],
      year: jDate.jy
    };
  };

  const getFullPersianDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const jDate = toJalaali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const weekDays = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];
    const months = [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
    const dayOfWeek = weekDays[date.getDay()];
    return `${dayOfWeek} ${jDate.jd.toLocaleString('fa-IR')} ${months[jDate.jm - 1]} ${jDate.jy.toLocaleString('fa-IR', { useGrouping: false })}`;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };


  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const days = getDaysInMonth();
  const weekDays = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
  const persianDate = getPersianDate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pb: 4 }}>
      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 800,
              mb: 1,
              background: 'linear-gradient(45deg, #fff 30%, #a5b4fc 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 10px 20px rgba(0,0,0,0.1)'
            }}
          >
            برنامه غذایی
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Vazir, sans-serif',
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 400
            }}
          >
            مدیریت برنامه غذایی روزانه رستوران
          </Typography>
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '15px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Month Selector */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => navigateMonth('prev')}
              sx={{
                height: '48px',
                borderRadius: '12px',
                fontFamily: 'Vazir, sans-serif',
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.4)',
                  background: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              ماه قبل
            </Button>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Vazir, sans-serif',
                fontWeight: 700,
                color: '#fff',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}
            >
              {persianDate.month} {toPersianNumber(persianDate.year)}
            </Typography>
            <Button
              variant="outlined"
              endIcon={<ArrowBackIcon />}
              onClick={() => navigateMonth('next')}
              sx={{
                height: '48px',
                borderRadius: '12px',
                fontFamily: 'Vazir, sans-serif',
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.4)',
                  background: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              ماه بعد
            </Button>
          </Box>
          {/* Week days header - separate grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 1.5,
              mb: 1.5,
              px: 0.5
            }}
          >
            {weekDays.map((day, index) => {
              const isFriday = day === 'جمعه';
              const isThursday = day === 'پنج‌شنبه';
              let headerBg = 'linear-gradient(45deg, #3b82f6, #60a5fa)';
              if (isFriday) {
                  headerBg = 'linear-gradient(45deg, #ef4444, #f87171)';
              } else if (isThursday) {
                  headerBg = 'linear-gradient(45deg, #f97316, #fb923c)';
              }

              return (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    fontWeight: 800,
                    fontFamily: 'Vazir, sans-serif',
                    color: 'white',
                    fontSize: '0.9rem',
                    background: headerBg,
                    borderRadius: '10px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>

          {/* Days grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              autoRows: 'minmax(140px, auto)', // Minimum height for rows
              gap: 1.5,
            }}
          >
            {days.map((dayData, index) => {
              if (dayData === null) {
                return <Box key={`empty-${index}`} />;
              }

              const { date, persianDay } = dayData;
              const dateStr = formatDateLocal(date);
              const dayItems = getMenuItemsForDate(dateStr);
              // Calculate total reservations for this day
              const totalReservations = dayItems.reduce((sum, item) => sum + (item.currentReservations || 0), 0);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const todayStr = formatDateLocal(today);
              const isToday = dateStr === todayStr;
              const dateOnly = new Date(date);
              dateOnly.setHours(0, 0, 0, 0);
              const isPast = dateOnly < today && !isToday;

              // Check if Thursday (5) or Friday (6) in Persian calendar
              // In Persian calendar: Saturday=0, Sunday=1, Monday=2, Tuesday=3, Wednesday=4, Thursday=5, Friday=6
              const dayOfWeek = date.getDay();
              const isThursday = dayOfWeek === 4; // Thursday in JS (0=Sunday, 4=Thursday)
              const isFriday = dayOfWeek === 5; // Friday in JS (0=Sunday, 5=Friday)

              const isOfficialHoliday = holidaysMap[dateStr]?.some(h => h.isHoliday) || false;

              let headerBg = 'linear-gradient(45deg, #3b82f6, #60a5fa)';
              if (isFriday || isOfficialHoliday) {
                  headerBg = 'linear-gradient(45deg, #ef4444, #f87171)';
              } else if (isThursday) {
                  headerBg = 'linear-gradient(45deg, #f97316, #fb923c)';
              }

              return (
                <Box
                  key={dateStr}
                  sx={{
                    minHeight: 0,
                    border: isToday ? '2px solid #ef4444' : '2px solid transparent',
                    borderRadius: '15px',
                    bgcolor: '#ffffff',
                    cursor: 'default',
                    opacity: isPast ? 0.6 : 1,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ 
                      background: headerBg, 
                      color: 'white',
                      px: 1.5,
                      height: '28px',
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      width: '100%',
                      boxSizing: 'border-box'
                  }}>
                    <Typography
                      variant={isToday ? 'subtitle2' : 'body2'}
                      sx={{
                        fontWeight: isToday ? 800 : 700,
                        color: 'white',
                        fontFamily: 'Vazir, sans-serif',
                        fontSize: isToday ? '1rem' : '0.9rem',
                        flexShrink: 0,
                        lineHeight: 1
                      }}
                    >
                      {toPersianNumber(persianDay)}
                    </Typography>
                    {holidaysMap[dateStr] && holidaysMap[dateStr].length > 0 && (() => {
                      const holidayText = holidaysMap[dateStr][0].title;
                      const isLong = holidayText.length > 25;
                      return (
                        <Box sx={{ overflow: 'hidden', ml: 1, flex: 1, position: 'relative', display: 'flex', alignItems: 'center', height: '100%' }}>
                          <Typography
                            sx={{
                              fontSize: '0.55rem',
                              fontWeight: 600,
                              lineHeight: 1,
                              color: 'rgba(255,255,255,0.95)',
                              fontFamily: 'Vazir, sans-serif',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              textAlign: 'left',
                              textOverflow: isLong ? 'unset' : 'ellipsis',
                              overflow: 'hidden',
                              width: isLong ? 'max-content' : '100%',
                              ...(isLong && {
                                animation: 'scrollText 8s linear infinite alternate',
                                '@keyframes scrollText': {
                                  '0%, 10%': { transform: 'translateX(0)' },
                                  '90%, 100%': { transform: 'translateX(-50%)' }
                                }
                              })
                            }}
                            title={holidaysMap[dateStr].map(h => h.title).join(' - ')}
                          >
                            {holidayText}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>

                  <Box sx={{ p: 1, flex: 1, display: 'flex', gap: 1 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {['breakfast', 'lunch', 'dinner'].map((type) => {
                        const items = dayItems.filter(i => i.mealType === type);
                        const hasItems = items.length > 0;
                        const label = type === 'breakfast' ? 'صبحانه' : type === 'lunch' ? 'ناهار' : 'شام';
                        const color = type === 'breakfast' ? '#eab308' : type === 'lunch' ? '#22c55e' : '#3b82f6';
                        const count = items.reduce((sum, item) => sum + (item.currentReservations || 0), 0);

                        return (
                          <Box
                            key={type}
                            sx={{
                              height: '24px',
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              px: 1,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              fontFamily: 'Vazir, sans-serif',
                              bgcolor: hasItems ? `${color}22` : '#f8fafc',
                              color: hasItems ? color : '#94a3b8',
                              border: '1px solid',
                              borderColor: hasItems ? `${color}44` : '#e2e8f0',
                              transition: 'all 0.2s',
                              cursor: isPast ? 'default' : 'pointer',
                              '&:hover': !isPast ? {
                                transform: 'scale(1.02)',
                                bgcolor: hasItems ? `${color}44` : '#f1f5f9',
                                boxShadow: `0 0 10px ${color}33`,
                              } : {},
                            }}
                            onClick={(e) => {
                              if (!isPast) {
                                e.stopPropagation();
                                handleDateSelect(dateStr, type as 'breakfast' | 'lunch' | 'dinner');
                              }
                            }}
                          >
                            <Box sx={{ flex: 1 }}>{label}</Box>
                            {hasItems && <Box sx={{ opacity: 0.8 }}>({toPersianNumber(count)})</Box>}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Total reservations count - left side */}
                    {totalReservations > 0 && (
                      <Box
                        sx={{
                          width: '32px',
                          bgcolor: '#10b981',
                          color: 'white',
                          borderRadius: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                          flexShrink: 0
                        }}
                      >
                        <RestaurantMenuIcon sx={{ fontSize: '1rem', mb: 0.2 }} />
                        <Typography sx={{ fontWeight: 800, fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}>
                          {toPersianNumber(totalReservations)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {dayItems.length === 0 && !isPast && (
                    <Box sx={{ flex: 1 }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '15px',
            bgcolor: '#1e293b',
            color: '#fff',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.2)'
          },
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Vazir, sans-serif',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#0f172a',
          py: 2
        }}>
          <Box>
            منو غذایی {mealType === 'breakfast' ? 'صبحانه' : mealType === 'lunch' ? 'ناهار' : 'شام'}
          </Box>
          <Typography sx={{ fontFamily: 'Vazir', fontSize: '1rem', fontWeight: 400, opacity: 0.8 }}>
            {getFullPersianDate(selectedDate)}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* List Existing Items */}

          <Box sx={{ mb: 4 }}>
            {getMenuItemsForDate(selectedDate).filter(i => i.mealType === mealType).length > 0 && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 120px',
                gap: 2,
                px: 2,
                py: 1,
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                opacity: 0.7,
                mb: 1,
              }}>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700 }}>ردیف</Typography>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700 }}>نام غذا</Typography>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700, textAlign: 'center' }}>قیمت کل</Typography>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700, textAlign: 'center' }}>سهم پرسنل</Typography>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700, textAlign: 'center' }}>ظرفیت</Typography>
                <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.8rem', fontWeight: 700, textAlign: 'left', pr: 2 }}>عملیات</Typography>
              </Box>
            )}
            {getMenuItemsForDate(selectedDate).filter(i => i.mealType === mealType).length === 0 && pendingItems.filter(p => p.date === selectedDate && p.mealType === mealType).length === 0 ? (
              <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontStyle: 'italic', opacity: 0.6 }}>
                هنوز غذایی برای این روز ثبت نشده است.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...getMenuItemsForDate(selectedDate).filter(i => i.mealType === mealType), ...pendingItems.filter(p => p.date === selectedDate && p.mealType === mealType)].map((item, idx) => {
                  const meal = meals.find(m => m.id === item.mealId);
                  const subsidizedPrice = meal ? meal.price - meal.staffShare : 0;
                  const displayPrice = item.price ?? meal?.price ?? 0;
                  const displayCorpPrice = item.isCorporate
                    ? (item.corporatePrice ?? subsidizedPrice)
                    : 0;
                  const displayStaffPrice = displayPrice - displayCorpPrice;
                  const isPending = item.id?.startsWith('pending-');

                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 120px',
                        gap: 2,
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: isPending ? 'rgba(79, 70, 229, 0.1)' : 'rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        border: isPending ? '1px solid rgba(79, 70, 229, 0.5)' : '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.9rem', opacity: 0.8 }}>
                        {(idx + 1).toLocaleString('fa-IR')}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={meal?.imageUrl}
                          alt={meal?.title}
                          variant="rounded"
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid rgba(255,255,255,0.1)',
                            bgcolor: 'rgba(255,255,255,0.05)'
                          }}
                        >
                          {meal?.title?.charAt(0)}
                        </Avatar>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontFamily: 'Vazir', fontWeight: 600, fontSize: '0.9rem' }}>
                            {meal?.title || 'نامشخص'}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.85rem', textAlign: 'center' }}>
                        {displayPrice.toLocaleString('fa-IR')}
                      </Typography>

                      <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.85rem', color: '#22c55e', textAlign: 'center' }}>
                        {displayStaffPrice.toLocaleString('fa-IR')}
                      </Typography>

                      <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.85rem', opacity: 0.8, textAlign: 'center' }}>
                        {(item.currentReservations || 0).toLocaleString('fa-IR')} / {(item.maxReservations || 0).toLocaleString('fa-IR')}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <input
                            type="checkbox"
                            checked={item.isCorporate}
                            onChange={() => handleToggleCorporate(item as MenuItem)}
                            style={{ cursor: 'pointer', width: 16, height: 16 }}
                          />
                          <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.65rem', opacity: 0.7 }}>سازمانی</Typography>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDelete(item.id!)}
                          sx={{ minWidth: 0, p: 0.5, fontFamily: 'Vazir', fontSize: '0.75rem' }}
                        >
                          حذف
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', mb: 2, fontWeight: 700, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 3 }}>
            افزودن غذای اصلی:
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 2,
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.02)',
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.05)',
            mb: 2
          }}>
            <FormControl sx={{ gridColumn: 'span 4' }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif', color: 'rgba(255,255,255,0.7)' }}>انتخاب غذای اصلی</InputLabel>
              <Select
                value={foodForm.mealId}
                onChange={(e) => {
                  const mealId = e.target.value;
                  const meal = meals.find(m => m.id === mealId);
                  setFoodForm({
                    ...foodForm,
                    mealId,
                    price: meal?.price || 0,
                    corporatePrice: meal ? (meal.price - meal.staffShare) : 0
                  });
                }}
                label="انتخاب غذای اصلی"
                renderValue={(selected) => {
                  const meal = meals.find(m => m.id === selected);
                  if (!meal) return <Box sx={{ opacity: 0.5 }}>بدون غذای اصلی</Box>;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={meal?.imageUrl} variant="rounded" sx={{ width: 24, height: 24, bgcolor: 'rgba(255,255,255,0.05)' }}>
                        {meal?.title?.charAt(0)}
                      </Avatar>
                      {meal?.title}
                    </Box>
                  );
                }}
                sx={{ fontFamily: 'Vazir, sans-serif', color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
              >
                <MuiMenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                  <Typography sx={{ opacity: 0.6 }}>انتخاب کنید...</Typography>
                </MuiMenuItem>
                {meals
                  .filter((meal) => mealType === 'breakfast' ? meal.category === 'breakfast' : (!meal.category || meal.category === 'food'))
                  .map((meal) => (
                  <MuiMenuItem key={meal.id} value={meal.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={meal?.imageUrl} variant="rounded" sx={{ width: 24, height: 24, bgcolor: 'rgba(255,255,255,0.05)' }}>
                        {meal?.title?.charAt(0)}
                      </Avatar>
                      <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
                        {meal.title} ({meal.price.toLocaleString('fa-IR')} ت)
                      </Typography>
                    </Box>
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{
                gridColumn: 'span 2',
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
              label="قیمت کل"
              value={foodForm.price === 0 ? '' : foodForm.price.toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) setFoodForm({ ...foodForm, price: val === '' ? 0 : Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{
                gridColumn: 'span 2',
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
              label="سهم پرسنل"
              value={(foodForm.price - foodForm.corporatePrice) === 0 ? '' : (foodForm.price - foodForm.corporatePrice).toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) {
                  const staffShare = val === '' ? 0 : Number(val);
                  setFoodForm({ ...foodForm, corporatePrice: Math.max(0, foodForm.price - staffShare) });
                }
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{
                gridColumn: 'span 2',
                '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input': { MozAppearance: 'textfield' } },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
              label="ظرفیت"
              value={foodForm.maxReservations}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value);
                if (val === '' || !isNaN(Number(val))) setFoodForm({ ...foodForm, maxReservations: Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <Button
              onClick={() => handleAddItem('food')}
              variant="contained"
              sx={{
                gridColumn: 'span 2',
                height: '56px',
                fontFamily: 'Vazir, sans-serif',
                fontWeight: 700,
                borderRadius: '12px',
                background: 'linear-gradient(45deg, #4f46e5, #818cf8)',
                boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
                '&:hover': { background: 'linear-gradient(45deg, #4338ca, #6366f1)' }
              }}
            >
              افزودن
            </Button>
          </Box>

          {/* Appetizer row */}
          <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', mb: 2, fontWeight: 700 }}>
            افزودن پیش‌غذا / سالاد:
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 2,
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.02)',
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.05)',
            mb: 2
          }}>
            <FormControl sx={{ gridColumn: 'span 4' }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif', color: 'rgba(255,255,255,0.7)' }}>انتخاب پیش‌غذا</InputLabel>
              <Select
                value={appetizerForm.mealId}
                onChange={(e) => {
                  const mealId = e.target.value;
                  const meal = meals.find(m => m.id === mealId);
                  setAppetizerForm({
                    ...appetizerForm,
                    mealId,
                    price: meal?.price || 0,
                    corporatePrice: meal ? (meal.price - meal.staffShare) : 0
                  });
                }}
                label="انتخاب پیش‌غذا"
                renderValue={(selected) => {
                  const meal = meals.find(m => m.id === selected);
                  if (!meal) return <Box sx={{ opacity: 0.5 }}>بدون پیش‌غذا</Box>;
                  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{meal?.title}</Box>;
                }}
                sx={{ fontFamily: 'Vazir, sans-serif', color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
              >
                <MuiMenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                  <Typography sx={{ opacity: 0.6 }}>انتخاب کنید...</Typography>
                </MuiMenuItem>
                {meals.filter(meal => meal.category === 'salad').map((meal) => (
                  <MuiMenuItem key={meal.id} value={meal.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.title} ({meal.price.toLocaleString('fa-IR')} ت)
                    </Typography>
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="قیمت کل"
              value={appetizerForm.price === 0 ? '' : appetizerForm.price.toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) setAppetizerForm({ ...appetizerForm, price: val === '' ? 0 : Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="سهم پرسنل"
              value={(appetizerForm.price - appetizerForm.corporatePrice) === 0 ? '' : (appetizerForm.price - appetizerForm.corporatePrice).toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) {
                  const staffShare = val === '' ? 0 : Number(val);
                  setAppetizerForm({ ...appetizerForm, corporatePrice: Math.max(0, appetizerForm.price - staffShare) });
                }
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input': { MozAppearance: 'textfield' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="ظرفیت"
              value={appetizerForm.maxReservations}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value);
                if (val === '' || !isNaN(Number(val))) setAppetizerForm({ ...appetizerForm, maxReservations: Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <Button
              onClick={() => handleAddItem('appetizer')}
              variant="contained"
              sx={{
                gridColumn: 'span 2',
                height: '56px',
                fontFamily: 'Vazir, sans-serif',
                fontWeight: 700,
                borderRadius: '12px',
                background: 'linear-gradient(45deg, #10b981, #34d399)',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                '&:hover': { background: 'linear-gradient(45deg, #059669, #10b981)' }
              }}
            >
              افزودن
            </Button>
          </Box>

          {/* Beverage row */}
          <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', mb: 2, fontWeight: 700 }}>
            افزودن نوشیدنی:
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: 2,
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.02)',
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <FormControl sx={{ gridColumn: 'span 4' }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif', color: 'rgba(255,255,255,0.7)' }}>انتخاب نوشیدنی</InputLabel>
              <Select
                value={beverageForm.mealId}
                onChange={(e) => {
                  const mealId = e.target.value;
                  const meal = meals.find(m => m.id === mealId);
                  setBeverageForm({
                    ...beverageForm,
                    mealId,
                    price: meal?.price || 0,
                    corporatePrice: meal ? (meal.price - meal.staffShare) : 0
                  });
                }}
                label="انتخاب نوشیدنی"
                renderValue={(selected) => {
                  const meal = meals.find(m => m.id === selected);
                  if (!meal) return <Box sx={{ opacity: 0.5 }}>بدون نوشیدنی</Box>;
                  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{meal?.title}</Box>;
                }}
                sx={{ fontFamily: 'Vazir, sans-serif', color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
              >
                <MuiMenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                  <Typography sx={{ opacity: 0.6 }}>انتخاب کنید...</Typography>
                </MuiMenuItem>
                {meals.filter(meal => meal.category === 'beverage').map((meal) => (
                  <MuiMenuItem key={meal.id} value={meal.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.title} ({meal.price.toLocaleString('fa-IR')} ت)
                    </Typography>
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="قیمت کل"
              value={beverageForm.price === 0 ? '' : beverageForm.price.toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) setBeverageForm({ ...beverageForm, price: val === '' ? 0 : Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="سهم پرسنل"
              value={(beverageForm.price - beverageForm.corporatePrice) === 0 ? '' : (beverageForm.price - beverageForm.corporatePrice).toLocaleString('en-US')}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value).replace(/,/g, '');
                if (val === '' || !isNaN(Number(val))) {
                  const staffShare = val === '' ? 0 : Number(val);
                  setBeverageForm({ ...beverageForm, corporatePrice: Math.max(0, beverageForm.price - staffShare) });
                }
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <TextField
              sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' }, '& input': { MozAppearance: 'textfield' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              label="ظرفیت"
              value={beverageForm.maxReservations}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value);
                if (val === '' || !isNaN(Number(val))) setBeverageForm({ ...beverageForm, maxReservations: Number(val) });
              }}
              InputProps={{ sx: { fontFamily: 'Vazir' } }}
            />

            <Button
              onClick={() => handleAddItem('beverage')}
              variant="contained"
              sx={{
                gridColumn: 'span 2',
                height: '56px',
                fontFamily: 'Vazir, sans-serif',
                fontWeight: 700,
                borderRadius: '12px',
                background: 'linear-gradient(45deg, #0ea5e9, #38bdf8)',
                boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)',
                '&:hover': { background: 'linear-gradient(45deg, #0284c7, #0ea5e9)' }
              }}
            >
              افزودن
            </Button>
          </Box>

          {/* گزینه‌های تکرار برای پر کردن خودکار خانه‌های خالی */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 3, p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repeatWeekly} 
                    onChange={(e) => {
                      setRepeatWeekly(e.target.checked);
                      if (e.target.checked) setRepeatDaily(false);
                    }} 
                    sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#818cf8' } }} 
                  />
                }
                label={
                  <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                    این منو برای همه روزهای {selectedDate ? ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'][new Date(selectedDate).getDay()] : ''} تا پایان ماه جاری قرار بده
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={repeatDaily} 
                    onChange={(e) => {
                      setRepeatDaily(e.target.checked);
                      if (e.target.checked) setRepeatWeekly(false);
                    }} 
                    sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#818cf8' } }} 
                  />
                }
                label={
                  <Typography sx={{ fontFamily: 'Vazir', fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                    این منو را برای همه روزهای ماه جاری قرار بده
                  </Typography>
                }
              />
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            variant="contained"
            onClick={() => setOpenModal(false)}
            sx={{ fontFamily: 'Vazir, sans-serif', bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMenu}
            disabled={pendingItems.filter(p => p.date === selectedDate && p.mealType === mealType).length === 0 && !repeatWeekly && !repeatDaily}
            sx={{
              fontFamily: 'Vazir, sans-serif',
              background: 'linear-gradient(45deg, #22c55e, #16a34a)',
              color: '#fff',
              '&:hover': { background: 'linear-gradient(45deg, #16a34a, #15803d)' }
            }}
          >
            ذخیره منو ({pendingItems.filter(p => p.date === selectedDate && p.mealType === mealType).length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Corporate Meal Validation */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '15px',
            bgcolor: '#1e293b',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.2)'
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', color: '#f87171' }}>هشدار سازمانی</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
            شما باید دقیقاً یک غذای سازمانی انتخاب کنید. آیا مطمئن هستید که می‌خواهید لیست را با وضعیت فعلی ذخیره کنید؟
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowConfirmDialog(false)} sx={{ fontFamily: 'Vazir, sans-serif', color: '#fff' }}>
            خیر، اصلاح می‌کنم
          </Button>
          <Button onClick={performBatchSave} sx={{ fontFamily: 'Vazir, sans-serif', color: '#f87171' }}>
            بله، ذخیره شود
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyMealPlanning;
